import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { jwtDecode } from "jwt-decode";
import path from "path-browserify";

import {
  getMessageFromError,
  handleResponse,
  requestWrapper,
} from "~/app/utils/requests";

import { BASE_URL, HEKA_PROXY } from "~/app/constant/env";

/**
 * Function to check if authorization or login data have the required "User" role
 * @param {Object} decodedToken Decoded JWT token object
 * @returns {boolean}
 */
const isAuthenticationValid = (decodedToken) =>
  decodedToken?.user_claims.roles.includes("Reporter");

/**
 * API SERVICES
 */

const apiVerifyAuthentication = () =>
  // First, we check it the refresh token is valid
  requestWrapper.post("/a12n/refresh/verify").then(() =>
    // Then the access token can be verified
    // It will be refreshed by the requestWrapper if needed since the refresh token is valid
    requestWrapper
      .post("/a12n/access/verify")
      // Then we check if the user can access the application checking his roles
      .then((res) => isAuthenticationValid(res.data))
  );

const apiGetProviders = () =>
  requestWrapper.get("/a12n/providers").then((res) => res.data);

const apiLogin = (data) =>
  requestWrapper
    .post("/a12n/login", data)
    .then((res) => {
      const decodedToken = jwtDecode(res.data.access_token);
      if (isAuthenticationValid(decodedToken)) {
        return res;
      } else {
        const customError = new Error("Permission denied");
        customError.response = {
          data: {
            message:
              "You don't have permission to access the application, please contact your administrator",
          },
        };
        return Promise.reject(customError);
      }
    })
    .catch((err) => {
      throw getMessageFromError(err);
    });

export const apiRequestPasswordChange = (email, new_user) => {
  const origin = HEKA_PROXY || window.location.origin;
  const confirmUrl =
    origin + path.join(BASE_URL, "/login/reset-password/{TOKEN}/{EMAIL}");
  return requestWrapper
    .post("/a12n/users/generate_password_signature/" + email, {
      confirm_url: confirmUrl,
      new_user: new_user,
    })
    .catch((err) => {
      throw getMessageFromError(err);
    });
};

const apiResetPassword = ({ email, ...data }) =>
  requestWrapper
    .post("/a12n/users/reset_password/" + email, data)
    .then((res) =>
      handleResponse(res, "Your password has been successfully updated")
    );

/**
 * HOOKS EXPORTS
 */

export const useVerifyAuthenticationQuery = (enabled) =>
  useQuery({
    queryKey: ["verifyAuthentication"],
    queryFn: apiVerifyAuthentication,
    enabled,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: Infinity,
  });

export const useProvidersQuery = () =>
  useQuery({
    queryKey: ["loginProviders"],
    queryFn: apiGetProviders,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: Infinity,
  });

export const useLoginMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: apiLogin,
    onSuccess: () => {
      // When the login is successfull, set verifyAuthentication query data to prevent token verification since fresh tokens were provided by the login itself
      queryClient.setQueryData(["verifyAuthentication", true]);
    },
  });
};

export const useRequestPasswordChangeMutation = () =>
  useMutation({
    mutationFn: apiRequestPasswordChange,
  });

export const useResetPasswordMutation = () =>
  useMutation({
    mutationFn: apiResetPassword,
  });
